import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { ConflictNotice } from '../ui/ConflictNotice';
import { HINTS_KEY, MANUAL_KEY } from './HintsProvider';
import { firstHeading, Markdown } from './markdown';
import { leaveGuarded, useUnsavedGuard, validForm } from '../ui/unsaved';
import { Notice } from '../ui/Notice';

type Item = {
  featureId: string;
  module: string;
  moduleTitle: string;
  hint: string;
  body: string;
  defaultHint: string;
  defaultBody: string;
  overridden: boolean;
  version: number;
};

const ADMIN_KEY = ['admin', 'hints'] as const;
const HINT_MAX = 140;
const label = (i: Item) => `${i.moduleTitle}: ${firstHeading(i.defaultBody) ?? i.featureId}`;

function Editor({ item, canEdit, onReload }: { item: Item; canEdit: boolean; onReload: () => void }) {
  const queryClient = useQueryClient();
  const [hint, setHint] = useState(item.hint);
  const [body, setBody] = useState(item.body);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    setHint(item.hint);
    setBody(item.body);
  }, [item]);

  const refresh = async () => {
    await Promise.all([ADMIN_KEY, HINTS_KEY, MANUAL_KEY].map((queryKey) => queryClient.invalidateQueries({ queryKey })));
  };
  const save = useMutation({
    mutationFn: () => api<Item>(`/api/admin/hints/${item.featureId}`, { method: 'PUT', body: { hint, body, version: item.version } }),
    onMutate: () => setSaved(false),
    onSuccess: async () => {
      await refresh();
      setSaved(true);
    },
  });
  const revert = useMutation({
    mutationFn: () => api<Item>(`/api/admin/hints/${item.featureId}?version=${item.version}`, { method: 'DELETE' }),
    onMutate: () => setSaved(false),
    onSuccess: async () => {
      await refresh();
      setSaved(true);
    },
  });
  const error = save.error ?? revert.error;
  const conflict = error instanceof ApiError && error.status === 409;
  const issue = (path: string) => (error instanceof ApiError ? fieldIssues(error).find((i) => i.path === path)?.message : undefined);
  const hintLength = [...hint].length;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const guardRef = useUnsavedGuard({
    dirty: canEdit && (hint !== item.hint || body !== item.body),
    save: async (el) => {
      if (!validForm(el)) return false;
      await save.mutateAsync();
      return true;
    },
  });

  return (
    <>
      <form ref={guardRef} className="panel" onSubmit={submit} aria-label={`Edycja: ${label(item)}`}>
        <h2>
          {firstHeading(item.defaultBody) ?? item.featureId} {item.overridden && <span className="chip">Zmieniony</span>}
        </h2>
        <p className="hint">Identyfikator funkcji: {item.featureId}</p>
        {conflict && <ConflictNotice onReload={() => { save.reset(); revert.reset(); onReload(); }} />}
        {error && !conflict && !(error instanceof ApiError && fieldIssues(error).length > 0) && (
          <p className="alert" role="alert">
            {error instanceof ApiError ? error.message : 'Nie udało się zapisać'}
          </p>
        )}
        {saved && (
          <Notice onClose={() => setSaved(false)}>
            Zapisano.
          </Notice>
        )}
        <label className="field">
          <span>Podpowiedź (jedna linijka)</span>
          <input value={hint} onChange={(e) => setHint(e.target.value)} readOnly={!canEdit} aria-describedby="hint-count" />
          <small id="hint-count" className={hintLength > HINT_MAX ? 'field-error' : 'hint'}>
            {hintLength} / {HINT_MAX} znaków
          </small>
          {issue('hint') && <span className="field-error">{issue('hint')}</span>}
        </label>
        <label className="field">
          <span>Opis w instrukcji</span>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} readOnly={!canEdit} rows={14} spellCheck lang="pl" />
          <small className="hint">Nagłówek: ### Tytuł. Lista: „- punkt” lub „1. punkt”. Pogrubienie: **tekst**.</small>
          {issue('body') && <span className="field-error">{issue('body')}</span>}
        </label>
        {canEdit && (
          <div className="actions">
            <button className="btn primary" type="submit" disabled={save.isPending || hintLength > HINT_MAX}>
              {save.isPending ? 'Zapisywanie…' : 'Zapisz'}
            </button>
            {item.overridden && (
              <button className="btn" type="button" disabled={revert.isPending} onClick={() => revert.mutate()}>
                Przywróć tekst fabryczny
              </button>
            )}
          </div>
        )}
      </form>
      <section className="panel" aria-label="Podgląd">
        <h2>Podgląd</h2>
        <p className="hint">Tak wygląda podpowiedź:</p>
        <div className="hint-pop hint-pop-static">
          <p className="hint-pop-text">{hint || '(pusta)'}</p>
          <span className="hint-pop-link">Więcej w instrukcji</span>
        </div>
        <p className="hint">A tak opis w instrukcji:</p>
        <div className="manual-section">
          <Markdown text={body} />
        </div>
      </section>
    </>
  );
}

export function HintsEditorPage() {
  const { me } = useMe();
  const items = useQuery({ queryKey: ADMIN_KEY, queryFn: () => api<{ items: Item[] }>('/api/admin/hints').then((r) => r.items) });
  const [featureId, setFeatureId] = useState<string>('');
  const selected = items.data?.find((i) => i.featureId === featureId) ?? items.data?.[0];
  const canEdit = !!me && can(me, 'hints.content', 'edit');

  return (
    <div className="page">
      <div className="page-head">
        <h1>Treść podpowiedzi</h1>
      </div>
      <p className="muted lede">
        Tu poprawiasz krótkie podpowiedzi i opisy w instrukcji. Po zapisie Ty widzisz zmianę od razu, pozostałe osoby najpóźniej po minucie od powrotu do aplikacji lub odświeżenia strony. Zawsze można wrócić do tekstu fabrycznego.
      </p>
      {items.isLoading && (
        <p className="muted" role="status">
          Wczytywanie…
        </p>
      )}
      {items.data && selected && (
        <>
          <label className="field perm-picker">
            <span>Funkcja</span>
            <select
              value={selected.featureId}
              onChange={(e) => {
                const next = e.target.value;
                leaveGuarded(() => setFeatureId(next));
              }}
            >
              {items.data.map((i) => (
                <option key={i.featureId} value={i.featureId}>
                  {label(i)}
                  {i.overridden ? ' (zmieniony)' : ''}
                </option>
              ))}
            </select>
          </label>
          <Editor key={selected.featureId} item={selected} canEdit={canEdit} onReload={() => void items.refetch()} />
        </>
      )}
    </div>
  );
}
